import { Avatar, Group, Text, Tooltip } from "@mantine/core"
import { useQuery } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { useEnsureUsers, useUsers } from "@/hooks"
import type { TSession } from "@/types"
import { sessionsAPI, setQueryCache } from "@/utils"
import { UserAvatar, UsersAvatarGroup } from "../people"
import { icons, TeamTag } from "../utils"

type TProps = Pick<TSession, "population" | "meta">

export function SessionPopulationShort({ population, meta }: TProps) {
  const { t } = useTranslation()
  const { user_ids = [], team_ids = [], onboarding } = meta
  const isManual = population !== "all"
  useEnsureUsers(isManual ? user_ids : [])
  const { findUsers } = useUsers()
  const res = (
    <Group justify="flex-end" gap={4}>
      {population === "all" ? (
        <>
          <Text span>{t("All")}</Text>
          <icons.Team size={16} />
        </>
      ) : (
        <>
          {user_ids.length > 0 && <UsersAvatarGroup users={findUsers(user_ids)} limit={3} size={32} withTooltip />}
          {team_ids?.map((teamId) => (
            <TeamTag key={teamId} id={teamId} />
          ))}
        </>
      )}
      {onboarding && (
        <Text c="dimmed" w="100%" size="xs">
          ({t("only onboarding")})
        </Text>
      )}
    </Group>
  )

  // Avatars already have their own tooltips, the group-level one overlaps them
  if (isManual && user_ids.length > 0) return res
  return <Tooltip label={population === "all" ? t("All employees") : t("Selected employees")}>{res}</Tooltip>
}

export function SessionPopulation({ id }: { id: string }) {
  const limit = 7
  const { data: participants = [] } = useQuery({
    queryKey: ["sessions-population", id],
    queryFn: () => sessionsAPI.population(id),
    ...setQueryCache(600e3),
  })

  return (
    <Avatar.Group>
      {participants.slice(0, limit).map((user) => (
        <UserAvatar key={user.id} user={user} size="lg" withLink withTooltip />
      ))}
      {participants.length > limit && (
        <Avatar size="lg" color="gray">
          +{participants.length - limit}
        </Avatar>
      )}
    </Avatar.Group>
  )
}
